import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useUpdateEffect } from 'ahooks';
import useProps from '../use-props';
import './style';

export default function (props) {
    const { children, show = true, avoid = true } = props;
    const { map } = useProps();
    const domRef = useRef();
    const domElementsRef = useRef([]);
    const lastExecutionTimeRef = useRef(0);

    // 注册DOM元素
    const registerDomElement = useCallback((element, options = {}) => {
        const { avoid = true } = options;
        const id = Math.random().toString(36).substr(2, 9); // 简单ID生成
        const elementInfo = { id, element, avoid };

        domElementsRef.current = [...domElementsRef.current, elementInfo];

        return () => {
            domElementsRef.current = domElementsRef.current.filter(el => el.id !== id);
        };
    }, []);

    // 碰撞检测函数
    const isColliding = (rect1, rect2) => {
        return !(rect1.right < rect2.left ||
                 rect1.left > rect2.right ||
                 rect1.bottom < rect2.top ||
                 rect1.top > rect2.bottom);
    };

    // 避让处理函数
    const handleAvoidance = useCallback(() => {
        // 限制执行频率，至少间隔50ms
        const now = Date.now();
        if (now - lastExecutionTimeRef.current < 50) {
            return;
        }
        lastExecutionTimeRef.current = now;

        if (!avoid) {
            // 如果避免功能关闭，显示所有元素
            domElementsRef.current.forEach(({ element }) => {
                if (element) {
                    element.style.visibility = 'visible';
                }
            });
            return;
        }

        // 获取所有DOM元素的位置信息
        const allElements = [];

        domElementsRef.current.forEach(({ id, element, avoid }) => {
            if (element) { // 不管当前显示状态如何，都参与计算
                const rect = element.getBoundingClientRect();

                const elementInfo = {
                    id,
                    element: element,
                    rect: rect,
                    area: rect.width * rect.height,
                    avoid: avoid
                };

                allElements.push(elementInfo);
            }
        });

        // 如果元素数量小于2，不需要避让，确保所有元素都显示
        if (allElements.length < 2) {
            allElements.forEach(({ element }) => {
                element.style.visibility = 'visible';
            });
            return;
        }

        // 分离必须显示的元素和可避让的元素
        const unavoidableElements = allElements.filter(el => !el.avoid);  // avoid=false的元素
        const avoidableElements = allElements.filter(el => el.avoid);      // avoid=true的元素

        // 标记需要隐藏的元素
        const toHide = new Set();

        // 检查所有元素之间的碰撞
        // 1. 必须显示的元素优先，与所有其他元素比较
        for (const unavoidable of unavoidableElements) {
            // 必须显示的元素与可避让元素碰撞时，隐藏可避让元素
            for (const avoidable of avoidableElements) {
                if (isColliding(unavoidable.rect, avoidable.rect)) {
                    toHide.add(avoidable.id);
                }
            }
        }

        // 2. 可避让元素之间按面积排序，面积大的优先显示
        avoidableElements.sort((a, b) => b.area - a.area);

        // 3. 检查所有可避让元素之间的碰撞关系
        for (let i = 0; i < avoidableElements.length; i++) {
            // 如果当前元素已经被标记为隐藏，则跳过
            if (toHide.has(avoidableElements[i].id)) continue;

            // 检查与所有后续元素的碰撞
            for (let j = i + 1; j < avoidableElements.length; j++) {
                // 如果比较的元素已经被标记为隐藏，则跳过
                if (toHide.has(avoidableElements[j].id)) continue;


                if (isColliding(avoidableElements[i].rect, avoidableElements[j].rect)) {
                    // 隐藏面积较小的元素（即j位置的元素）
                    toHide.add(avoidableElements[j].id);
                }
            }
        }

        // 应用显示/隐藏
        allElements.forEach(({ id, element, avoid: elementAvoid }) => {
            if (!elementAvoid) {
                // avoid=false的元素始终显示
                element.style.visibility = 'visible';
            } else {
                // avoid=true的元素根据碰撞检测结果决定显示或隐藏
                element.style.visibility = toHide.has(id) ? 'hidden' : 'visible';
            }
        });
    }, [avoid]);

    // 控制显示/隐藏
    useUpdateEffect(() => {
        if (domRef.current) {
            domRef.current.style.visibility = show ? 'visible' : 'hidden';
        }

        // 当显示状态改变时重新计算避让
        if (show && avoid) {
            setTimeout(handleAvoidance, 10);
        }
    }, [show, avoid]);

    // 监听地图事件
    useEffect(() => {
        if (!map) return;

        let avoidanceTimer = null;

        // 处理地图变化
        const handleMapChange = () => {
            // 清除之前的定时器
            if (avoidanceTimer) {
                clearTimeout(avoidanceTimer);
            }

            // 延迟执行避让处理，确保DOM已经更新位置
            avoidanceTimer = setTimeout(() => {
                if (avoid) {
                    handleAvoidance();
                }
            }, 100);
        };


        // 监听地图移动事件
        map.camera.moveEnd.addEventListener(handleMapChange);

        // 初始计算一次
        setTimeout(() => {
            if (avoid) {
                handleAvoidance();
            }
        }, 200);

        return () => {
            if (map && map.camera) {
                map.camera.moveEnd.removeEventListener(handleMapChange);
            }
            if (avoidanceTimer) {
                clearTimeout(avoidanceTimer);
            }
        };
    }, [handleAvoidance, avoid]);

    // 向子组件传递上下文
    const childrenWithProps = React.Children.map(children, child => {
        if (React.isValidElement(child)) {
            return React.cloneElement(child, {
                _registerDomLayer: registerDomElement
            });
        }
        return child;
    });

    return (
        <div className="cs-map-dom-layer" ref={domRef} style={{ display: show ? 'block' : 'none' }}>
            {childrenWithProps}
        </div>
    );
}
